<script setup lang="ts">
import { computed } from 'vue';

import IconLock from '@/shared/components/UI/Icons/IconLock.vue';
import { useCartShipping } from '@/shared/composables/cart/useCartShipping';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { getCountryCode } from '@/shared/utils/country';
import {
  formattedPriceWithDecimals as formattedPrice,
  parseCompareAtPrice
} from '@/shared/utils/shopify/money-formatter';

type Props = {
  shippingThreshold?: number; // in cents - free shipping threshold
  lowestShippingPrice?: number; // in cents - lowest shipping price
  showSalesTaxMessage?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  shippingThreshold: 0,
  lowestShippingPrice: 0,
  showSalesTaxMessage: false
});

const cartStore = useCartStore();
const { t } = useTranslations();
const { shippingSubtotal, hasPhysicalProducts, totalCartLevelDiscounts } =
  useCartShipping();

// Get sales tax message based on country
// Note: This is only supposed to be set for US and CA markets only
const salesTaxMessage = computed(() => {
  if (!props.showSalesTaxMessage) {
    return null;
  }

  const salesTaxSettings = window.Hobbii?.salesTaxSettings;
  if (!salesTaxSettings) {
    return null;
  }

  const countryCode = getCountryCode();
  const isCanada = countryCode === 'CA';

  const message = isCanada
    ? salesTaxSettings.priceDisclaimerForCA
    : salesTaxSettings.priceDisclaimerForUS;

  // Only return message if it's not blank (matching Liquid: `message != blank`)
  return message && message.trim() !== '' ? message : null;
});

// Calculate shipping cost accounting for cart-level discounts (matches MiniCartLinesSummary)
const calculatedShippingCost = computed(() => {
  if (!hasPhysicalProducts.value) {
    return 0;
  }

  const effectiveSubtotal =
    shippingSubtotal.value - totalCartLevelDiscounts.value;

  if (
    props.shippingThreshold > 0 &&
    effectiveSubtotal >= props.shippingThreshold
  ) {
    return 0;
  }

  return props.lowestShippingPrice || 0;
});

// Calculate original cart subtotal
// Uses max of original_line_price or compare_at_price * quantity for each item
const originalCartSubtotal = computed(() => {
  if (!cartStore.cart?.items) {
    return 0;
  }

  return cartStore.cart.items.reduce((total, item) => {
    const originalPrice = item.original_price || 0;
    const originalLinePrice = item.original_line_price || 0;

    // Get compare_at_price from variant if available
    // Shopify API returns compare_at_price as a string (e.g., "25.00") or number
    const compareAtPrice = item.variant?.compare_at_price
      ? parseCompareAtPrice(item.variant.compare_at_price)
      : 0;

    // Choose largest of original_price or compare_at_price
    const maxOriginalOrCompareAtLinePrice =
      compareAtPrice > originalPrice
        ? compareAtPrice * (item.quantity || 1)
        : originalLinePrice;

    return total + maxOriginalOrCompareAtLinePrice;
  }, 0);
});

// Calculate totals including shipping
const originalCartTotal = computed(() => {
  return originalCartSubtotal.value + calculatedShippingCost.value;
});

const finalCartTotal = computed(() => {
  const cartTotal = cartStore.cart?.total_price || 0;
  return cartTotal + calculatedShippingCost.value;
});

// Check if cart has discount
const cartHasDiscount = computed(() => {
  return originalCartTotal.value > finalCartTotal.value;
});

// Format prices
const formattedOriginalTotal = computed(() => {
  return formattedPrice(originalCartTotal.value);
});

const formattedFinalTotal = computed(() => {
  return formattedPrice(finalCartTotal.value);
});
</script>

<template>
  <div
    class="relative flex grow-0 flex-col gap-1 border-t border-gray-lightest px-4 pb-0 pt-2 text-sm shadow-[0px_-10px_25px_0_rgba(0,0,0,0.15)] md:px-8"
  >
    <div class="flex items-center justify-between">
      <div class="">
        <div class="text-base font-bold">
          {{ t('cart.total') }}
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="salesTaxMessage" class="text-xs text-gray-400">
            {{ salesTaxMessage }}
          </span>
          <span v-else>{{ t('cart.total_disclaimer') }}</span>
        </div>
      </div>

      <div class="flex items-end gap-2">
        <template v-if="cartHasDiscount">
          <span class="-mb-px inline-block text-sm text-black-500 line-through">
            {{ formattedOriginalTotal }}
          </span>
          <strong class="text-2xl leading-none text-pink">
            {{ formattedFinalTotal }}
          </strong>
        </template>
        <strong v-else class="text-2xl leading-none">
          {{ formattedFinalTotal }}
        </strong>
      </div>
    </div>

    <a
      class="mt-1 flex items-center justify-center gap-1 rounded-lg bg-pink p-3 text-center text-lg font-bold text-white transition-colors duration-300 hover:bg-pink-darker hover:no-underline group-[.is-loading]:pointer-events-none group-[.is-loading]:opacity-70"
      href="/checkout"
    >
      <IconLock class="text-2xl" />
      {{ t('shopping_cart.text_cart') }}
    </a>
  </div>
</template>
